import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ScanSearch, Undo2, RotateCw, Trash2, Plus, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { Switch } from "@/components/ui/switch";
import { AdminTitle } from "@/admin/AdminLayout";
import { useTitles } from "@/admin/LinkPickerDialog";
import { ReviewLinks, BatchReport, initialReview } from "@/admin/ReviewLinks";

const when = (d) => (d ? new Date(d).toLocaleString() : "");

const Stat = ({ label, items, testid, render }) => (
  <details data-testid={testid} className="vellum p-4">
    <summary className="cursor-pointer font-heading text-sm"><b className="font-display text-2xl mr-2" data-testid={`${testid}-count`}>{items.length}</b>{label}</summary>
    <div className="mt-3 max-h-64 overflow-y-auto text-sm">{items.length ? items.map(render) : <p className="italic">None.</p>}</div>
  </details>
);

const Aliases = () => {
  const titles = useTitles();
  const [list, setList] = useState([]);
  const [n, setN] = useState({ alias: "", article_id: "" });
  const load = () => api.get("/admin/links/aliases").then((r) => setList(r.data));
  useEffect(() => { load(); }, []);
  const add = async (e) => { e.preventDefault(); try { await api.post("/admin/links/aliases", n); setN({ alias: "", article_id: "" }); load(); } catch (err) { toast.error(errMsg(err)); } };
  const save = async (a, patch) => { try { await api.put(`/admin/links/aliases/${a.id}`, { ...a, ...patch }); load(); } catch (err) { toast.error(errMsg(err)); } };
  const del = async (a) => { await api.delete(`/admin/links/aliases/${a.id}`); load(); };
  return (
    <section data-testid="alias-manager" className="vellum p-6">
      <p className="eyebrow">Aliases</p>
      <p className="text-sm italic text-[#4a3a2a] mb-4">Other names that should link to one specific article. A red warning means the name also matches another article or title, so it's never linked automatically.</p>
      <form onSubmit={add} className="flex flex-wrap gap-2 mb-4">
        <input data-testid="alias-new-text" required placeholder="Alias, e.g. the Drakehelms" value={n.alias} onChange={(e) => setN({ ...n, alias: e.target.value })} className="admin-input flex-1 min-w-[12rem]" />
        <select data-testid="alias-new-article" required value={n.article_id} onChange={(e) => setN({ ...n, article_id: e.target.value })} className="admin-input flex-1 min-w-[12rem]">
          <option value="">Points to article…</option>{titles.map((t) => <option key={t.id} value={t.id}>{t.title}</option>)}
        </select>
        <button data-testid="alias-add" className="btn-illum theme-flame"><Plus className="w-4 h-4" /> Add alias</button>
      </form>
      {list.map((a) => (
        <div key={a.id} data-testid={`alias-row-${a.id}`} className="flex flex-wrap items-center gap-2 py-2 border-b border-[#c9a227]/25">
          <input defaultValue={a.alias} data-testid={`alias-edit-${a.id}`} onBlur={(e) => e.target.value !== a.alias && save(a, { alias: e.target.value })} className="admin-input flex-1 min-w-[10rem] !py-1" />
          <select value={a.article_id} data-testid={`alias-article-${a.id}`} onChange={(e) => save(a, { article_id: e.target.value })} className="admin-input flex-1 min-w-[10rem] !py-1">
            {titles.map((t) => <option key={t.id} value={t.id}>{t.title}</option>)}
          </select>
          {a.conflict && <span data-testid={`alias-conflict-${a.id}`} className="text-xs font-heading text-[#991b1b] inline-flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> Conflict</span>}
          <button onClick={() => del(a)} data-testid={`alias-delete-${a.id}`} className="p-2 text-[#991b1b] hover:bg-[#991b1b]/10"><Trash2 className="w-4 h-4" /></button>
        </div>
      ))}
    </section>
  );
};

const Batches = ({ batches, reload }) => {
  const act = async (b, what) => {
    if (what === "rollback" && !window.confirm("Remove every link this batch added? Later edits to those articles are kept.")) return;
    try { const { data } = await api.post(`/admin/links/batches/${b.id}/${what}`); toast[data.errors?.length || data.status === "partial" ? "warning" : "success"](what === "dismiss" ? "Dismissed" : what === "rollback" ? `Rollback: ${data.status}` : `Retry: ${data.status}`); reload(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <section data-testid="batch-log" className="vellum p-6">
      <p className="eyebrow mb-3">Linking history (audit log)</p>
      {batches.length === 0 && <p className="italic text-sm">No linking batches yet.</p>}
      {batches.map((b) => (
        <details key={b.id} data-testid={`batch-${b.id}`} className="border-b border-[#c9a227]/25 py-2">
          <summary className="cursor-pointer text-sm flex flex-wrap items-center gap-2">
            <b className="font-heading">{when(b.created_at)}</b> <span>{b.kind === "site" ? "Full-site scan" : "Article publish"}</span>
            <span data-testid={`batch-status-${b.id}`} className={`text-xs font-heading font-bold uppercase ${b.status === "applied" ? "text-[#2f6b3a]" : "text-[#991b1b]"}`}>{b.status.replace("_", " ")}</span>
            <span className="text-xs">{b.items.reduce((n, i) => n + (i.status === "applied" ? i.links.length : 0), 0)} links in {b.items.filter((i) => i.status === "applied").length} articles</span>
            <span className="ml-auto flex gap-2">
              {!b.dismissed && ["partial", "failed", "rollback_partial"].includes(b.status) && <button onClick={() => act(b, "dismiss")} data-testid={`batch-dismiss-${b.id}`} className="text-xs underline">Dismiss</button>}
              {!b.dismissed && ["partial", "failed"].includes(b.status) && <button onClick={() => act(b, "retry")} data-testid={`batch-retry-${b.id}`} className="text-xs underline inline-flex items-center gap-1"><RotateCw className="w-3 h-3" /> Retry</button>}
              {!b.dismissed && ["applied", "partial", "rollback_partial"].includes(b.status) && <button onClick={() => act(b, "rollback")} data-testid={`batch-rollback-${b.id}`} className="text-xs underline text-[#991b1b] inline-flex items-center gap-1"><Undo2 className="w-3 h-3" /> Undo batch</button>}
            </span>
          </summary>
          <div className="pl-4 py-2 text-sm"><BatchReport report={b} />
            {b.rejected?.length > 0 && <p className="mt-2 text-xs italic">Rejected: {b.rejected.map((r) => `“${r.term}” in ${r.source_title}`).join("; ")}</p>}
            {b.rollback_errors?.length > 0 && <p className="mt-2 text-xs text-[#991b1b]">Rollback issues: {b.rollback_errors.join("; ")}</p>}
          </div>
        </details>
      ))}
    </section>
  );
};

export default function AdminLinks() {
  const [d, setD] = useState(null);
  const [scan, setScan] = useState(null);
  const [st, setSt] = useState(null);
  const [busy, setBusy] = useState(false);
  const [report, setReport] = useState(null);
  const load = useCallback(() => api.get("/admin/links/dashboard").then((r) => setD(r.data)), []);
  useEffect(() => { load(); }, [load]);
  const openScan = (s) => { setScan(s); setSt(initialReview(s.proposals)); setReport(null); };
  const runScan = async () => { setBusy(true); try { openScan((await api.post("/admin/links/scan-site")).data); load(); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); } };
  const apply = async (chosen, rejected) => {
    if (!chosen.length) { toast.error("Select at least one link"); return; }
    setBusy(true);
    try { setReport((await api.post("/admin/links/apply", { proposals: chosen, rejected, scan_id: scan.id })).data); setScan(null); load(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const allow = async (v) => { await api.put("/admin/links/settings", { allow_full_site: v }); load(); };
  if (!d) return <p className="font-display text-2xl">Unrolling…</p>;
  const art = (x, i) => <p key={i}><Link to={`/wiki/${x.slug}`} target="_blank" className="underline">{x.title}</Link>{x.target && <> → <code>{x.target}</code> ({x.reason})</>}{x.dest && <> → {x.dest} ×{x.count}</>}</p>;

  return (
    <div data-testid="admin-links">
      <AdminTitle eyebrow="Two-way lore linker" title="Lore Links">
        <button data-testid="links-full-scan" onClick={runScan} disabled={busy} className="btn-illum theme-flame disabled:opacity-60"><ScanSearch className="w-4 h-4" /> {busy ? "Scanning…" : "Full-Site Link Scan"}</button>
      </AdminTitle>
      <label className="vellum p-4 mb-6 flex items-start gap-3">
        <Switch data-testid="links-allow-full-site" checked={d.allow_full_site} onCheckedChange={allow} />
        <span><b className="font-heading">Allow full-site changes</b><span className="block text-sm italic text-[#4a3a2a]">Scans always work. Applying a full-site scan to many articles stays locked until this is on. Publishing a single article always shows its own review.</span></span>
      </label>
      {report && <div className="vellum p-6 mb-6"><p className="eyebrow mb-2">Completion report</p><BatchReport report={report} /></div>}
      {scan ? (
        <section className="mb-8">
          {scan.proposals.length === 0 ? <p data-testid="links-scan-empty" className="vellum p-6 italic">No new links found. Everything that can be linked already is.</p>
            : <ReviewLinks proposals={scan.proposals} st={st} set={setSt} busy={busy} onBack={() => setScan(null)} backLabel="Close review (kept as pending)" onConfirm={apply} confirmLabel="Apply selected links"
                locked={!d.allow_full_site && 'Applying is locked. Switch on "Allow full-site changes" above.'} />}
        </section>
      ) : (
        <>
          <div className="grid sm:grid-cols-2 xl:grid-cols-4 gap-4 mb-6">
            <Stat label="articles with no incoming links" items={d.orphans} testid="links-orphans" render={art} />
            <Stat label="broken internal links" items={d.broken} testid="links-broken" render={art} />
            <Stat label="repeated links to the same page" items={d.duplicates} testid="links-duplicates" render={art} />
            <div data-testid="links-pending" className="vellum p-4">
              <p className="font-heading text-sm"><b className="font-display text-2xl mr-2" data-testid="links-pending-count">{d.pending_scans.reduce((n, s) => n + s.count, 0)}</b>pending proposals</p>
              {d.pending_scans.map((s) => <button key={s.id} data-testid={`links-open-scan-${s.id}`} onClick={async () => openScan((await api.get(`/admin/links/scans/${s.id}`)).data)} className="text-xs underline block mt-1">Review scan from {when(s.created_at)} ({s.count})</button>)}
              <p className="text-xs mt-2">{d.batches.filter((b) => !b.dismissed && ["partial", "failed", "rollback_partial"].includes(b.status)).length} failed or incomplete batch(es)</p>
            </div>
          </div>
          <div className="grid xl:grid-cols-2 gap-6"><Aliases /><Batches batches={d.batches} reload={load} /></div>
        </>
      )}
    </div>
  );
}
